import {action, observable, makeObservable} from 'mobx';
import {Form} from './Form';
import {
    checkAndSaveHtml,
    exitSubForm,
    openSubForm,
    refreshByRefreshLines,
    saveRecordValue,
    startRecordEdit,
    updateFieldValue
} from '../../features/Form/actions/form';
import {mapFormToBreadcrumbs} from '../../features/Form/utils/mapFormToBreadcrumbs';
import {BreadCrumbsList} from '../../features/Form/types';
import {RecordEditState, UpdateActions} from './enums';
import {getUpdateActionType, mapValueByColumnType, findFormByLevel, getOrigValue} from './utils';
import {priority} from '@priority-software/priority-proto-api/lib';
import {handleScreenMessage, callLinkedActions} from '../../features/Form/actions/screen';
import {Field} from './Field';
import {UpdateStatusTypes} from './API_Locker';
import {changeLockerStatus} from '../../features/Form/components/ScreenLocker/actions';
import {noDefaultMessageHandling, recordDelete} from '../../services/form/form';
import {Record} from './Record';
import {undoAll, undoLastChange} from '../../features/Form/actions/undo';
import {Options} from '../../services/proto/core/server';
import {showFormClientSideMessage} from '../../features/Form/actions/messages';
import texts from './texts';
import {formsStore} from './FormsStore';
import {ensureKeysPresent} from './utils/ensureKeysPresent';

export enum APIActionStatus {
    blocked = 0,
    passed = 1
}

export class Screen {
    @observable session: string;
    @observable activeLevel: number;
    @observable breadcrumbs: BreadCrumbsList = [];
    @observable isNavigationBlocked: boolean = false; // Do not toggle this property directly, use `lockScreenKeyboardNavigation` function instead
    @observable isRecordDialog: boolean = false;

    form: Form;
    editMode: RecordEditState = RecordEditState.default;
    zoomSource: Screen | null = null;
    zoomChild: Screen | null = null;
    standAlone: boolean;

    constructor(form: Form, zoomSource: Screen = null, standAlone: boolean = false) {
        makeObservable(this);
        this.session = form.session;
        this.activeLevel = form.level || 0;
        this.form = form;
        this.zoomSource = zoomSource;
        this.setBreadcrumbs();
        this.standAlone = standAlone;
    }

    setZoomChild = (zoomChild: Screen) => {
        this.zoomChild = zoomChild;
    };

    getActiveForm = (): Form => {
        return findFormByLevel(this.form, this.activeLevel);
    };
    @action
    setRecordDialog = (flag: boolean) => (this.isRecordDialog = flag);

    onFieldFocusChange = async (
        payload: {newCellIndex: number; newRowIndex: number; level: number},
        focusAction: (cellIndex: number, rowIndex: number) => void
    ) => {
        const {level, newCellIndex, newRowIndex} = payload;
        const activeForm = this.getActiveForm();
        if (!activeForm) return APIActionStatus.blocked;

        const skipCheck =
            level === activeForm.level &&
            newRowIndex === activeForm.currentRow &&
            newCellIndex === activeForm.currentColumn?.index;

        if (!skipCheck) {
            const valueActionType = getUpdateActionType(
                activeForm,
                !skipCheck,
                this.editMode,
                payload
            );
            const isLastRow = activeForm.currentRow === activeForm.records.length - 1;
            const isValidAction = await this.checkAndSave(valueActionType);
            const currentRow = activeForm.records[activeForm.currentRow];

            // Clear temporary rows
            let nextRowIndex = newRowIndex;

            if (
                currentRow?.isTemporary &&
                this.editMode === RecordEditState.default &&
                (activeForm.currentRow !== payload.newRowIndex ||
                    activeForm.level !== payload.level)
            ) {
                this.removeRecordFromClient(activeForm.currentRow);

                if (
                    activeForm.level === payload.level &&
                    activeForm.currentRow < payload.newRowIndex
                ) {
                    nextRowIndex = newRowIndex - 1;
                }
            }

            if (isValidAction) {
                const shouldChangeLevel = activeForm.level !== level;
                let nextActionAllowed = APIActionStatus.passed;
                if (shouldChangeLevel) {
                    nextActionAllowed = await this.handleFormLevel(level);
                }

                if (nextActionAllowed) {
                    // change focus to the last empty row
                    if (isLastRow && !shouldChangeLevel) {
                        const isSaveAction =
                            valueActionType === UpdateActions.save ||
                            valueActionType === UpdateActions.update_save;
                        if (isSaveAction) {
                            nextRowIndex = activeForm.records.length - 1;
                        }
                    }
                    // Change focus if the level passed validation
                    focusAction(newCellIndex, nextRowIndex);
                }
                return nextActionAllowed;
            }
            return isValidAction;
        }
        return APIActionStatus.passed;
    };

    checkAndSave = async (updateType?: UpdateActions): Promise<APIActionStatus> => {
        const currentForm = this.getActiveForm();
        const type = updateType || getUpdateActionType(currentForm, true, this.editMode);

        if (type === UpdateActions.saveHtml) {
            return await checkAndSaveHtml(currentForm);
        } else if (type === UpdateActions.update) {
            return await this.fieldUpdate(null, true);
        } else if (type === UpdateActions.save) {
            changeLockerStatus(this.session, UpdateStatusTypes.updating, false);
            const isOk = await this.saveRecord();
            changeLockerStatus(
                this.session,
                isOk ? UpdateStatusTypes.saved : UpdateStatusTypes.none,
                false
            );
            return isOk;
        } else if (type === UpdateActions.update_save) {
            changeLockerStatus(this.session, UpdateStatusTypes.updating, false);
            const isOk = await callLinkedActions(
                () => this.fieldUpdate(null, true),
                this.saveRecord
            );
            changeLockerStatus(
                this.session,
                isOk ? UpdateStatusTypes.saved : UpdateStatusTypes.none,
                false
            );
            return isOk;
        }
        return APIActionStatus.passed;
    };

    checkLevel = async (level: number) => {
        const switchLevel = async () => {
            if (this.activeLevel !== level) {
                // Switching between levels on the server
                if (this.activeLevel > level) {
                    const levelsCount = this.activeLevel - level;
                    for (let index = 0; index < levelsCount; index++) {
                        await exitSubForm(this.session);
                    }
                    return APIActionStatus.passed;
                } else if (this.activeLevel < level) {
                    const levelsCount = level - this.activeLevel;
                    // We support only switching between parent and direct child form. Maximum depth is 1 per action.
                    return await new Promise<APIActionStatus>(async resolve => {
                        const blockAction = (fallbackLevel: number) => {
                            // Switching to the nearest valid level when something goes wrong;
                            this.setActiveLevel(fallbackLevel);
                            resolve(APIActionStatus.blocked);
                        };

                        for (let index = 0; index < levelsCount; index++) {
                            const form = findFormByLevel(this.form, this.activeLevel);
                            const subForm = form?.activeSubForm;

                            if (form && form.records[form.currentRow]?.keys && subForm) {
                                const isOk = await openSubForm(
                                    form,
                                    subForm.ename,
                                    subForm.form.Ishtml
                                ); // entering subform form
                                if (!isOk) {
                                    blockAction(index);
                                    break;
                                } else if (index === levelsCount - 1) {
                                    resolve(APIActionStatus.passed);
                                }
                            } else {
                                blockAction(index);
                                break;
                            }
                        }
                    });
                }
            }

            return APIActionStatus.passed;
        };
        return await callLinkedActions(async () => {
            const type = getUpdateActionType(this.getActiveForm(), true, this.editMode, {
                isExit: this.activeLevel !== level
            });
            return await this.checkAndSave(type);
        }, switchLevel);
    };

    saveRecord = async (
        options: Partial<Options> = noDefaultMessageHandling
    ): Promise<APIActionStatus> => {
        const saveRes: priority.netitems.form.RecordSaveRs = await saveRecordValue(
            this.session,
            options
        );
        const {currentRow, updateRecordValues} = this.getActiveForm();
        updateRecordValues(currentRow, saveRes.Record?.Fields);
        // if message has error or warning message, we should let user to fix & update current value on the server
        this.checkRecordErrorState(saveRes.Messages);
        await this.processRefreshRs(saveRes);

        // Track the final response (either original or from warning approval)
        let finalResponse = saveRes;

        return await handleScreenMessage(
            saveRes,
            {
                onWarningConfirm: approvalResponse => {
                    // Capture the approval response with final keys
                    finalResponse = approvalResponse;
                }
            },
            () => {
                if (finalResponse.Record) {
                    const activeForm = this.getActiveForm();
                    // Use finalResponse instead of saveRes - now has the server-assigned keys
                    activeForm.updateRecordValues(
                        activeForm.currentRow,
                        finalResponse.Record.Fields,
                        activeForm.currentRecord?.isNew
                    );
                }
                this.onRecordSaveSuccess();
            },
            false,
            this.session
        );
    };

    processRefreshRs = async (
        saveRes:
            | priority.netitems.form.RecordSaveRs
            | priority.netitems.form.RecordDeleteRs
            | priority.netitems.form.WarningApproveRs
    ) => {
        if (saveRes.Refresh) {
            const refreshData = saveRes.Refresh;
            const activeForm = this.getActiveForm();
            if (refreshData[activeForm.ename]) {
                await refreshByRefreshLines(activeForm, refreshData[activeForm.ename]);
            }

            if (activeForm.parentForm && refreshData[activeForm.parentForm.ename]) {
                this.setParentFormsRefreshCode(activeForm.parentForm, refreshData);
            }
        }
    };

    setParentFormsRefreshCode = (
        form: Form,
        refreshData: {[k: string]: priority.netitems.form.RefreshLines}
    ) => {
        form.refreshCode = refreshData[form.ename];
        if (form.parentForm) {
            this.setParentFormsRefreshCode(form.parentForm, refreshData);
        }
    };

    recordDeleteAction = async (record: Record): Promise<APIActionStatus> => {
        if (record.isNew || record.isTemporary) {
            const undoHandle = !record.keys ? undoLastChange : undoAll;
            await undoHandle(this);

            return Promise.resolve(APIActionStatus.passed);
        } else {
            const deleteRes = await recordDelete(this.session, record.keys);
            this.checkRecordErrorState(deleteRes.Messages);
            await this.processRefreshRs(deleteRes);
            return handleScreenMessage(
                deleteRes,
                {
                    onClose: () => {
                        this.editMode = RecordEditState.default;
                    }
                },
                () => {
                    const activeForm = this.getActiveForm();
                    if (activeForm) {
                        activeForm.deleteClientRecord(record.recordIndex);
                        activeForm.setAllRecordsCount(activeForm.allRecordsCount - 1);
                    }
                },
                false,
                this.session
            );
        }
    };

    onRecordSaveSuccess = () => {
        this.editMode = RecordEditState.default;
        const activeForm: Form = this.getActiveForm();

        if (activeForm) {
            if (activeForm.currentRecord.isNew) {
                activeForm.setAllRecordsCount(activeForm.allRecordsCount + 1);
            }
            activeForm.currentRecord.isNew = false;
            activeForm.currentRecord.isTemporary = false;

            const isLastRow = activeForm.currentRow === activeForm.records.length - 1;
            if (isLastRow) {
                activeForm.insertEmptyRecord(activeForm.records.length);
            }
            changeLockerStatus(this.session, UpdateStatusTypes.saved, false);
        }
    };

    startRecordEdit = async (
        activeForm: Form,
        options: Partial<Options> = null
    ): Promise<APIActionStatus> => {
        if (this.editMode === RecordEditState.default) {
            const editRs = await startRecordEdit(activeForm, options);

            return await handleScreenMessage(
                editRs,
                {},
                async () => (this.editMode = RecordEditState.edit),
                false,
                this.session
            );
        }
        return APIActionStatus.passed;
    };

    updateRelatedField = async (relatedFieldId, value) => {
        const activeForm = this.getActiveForm();
        if (activeForm) {
            const currentRecord = activeForm.records[activeForm.currentRow];
            const relatedField = currentRecord.getField(relatedFieldId);

            relatedField.setCurrentValue(value);
            await this.fieldUpdate(relatedField, true);
        }
    };

    fieldUpdate = async (
        relatedField?: Field,
        checkDuplicates?: boolean
    ): Promise<APIActionStatus> => {
        const activeForm = this.getActiveForm();
        const allowed = await this.startRecordEdit(activeForm);

        if (allowed) {
            const activeField = relatedField || activeForm.activeField();
            const currentColumn = !relatedField
                ? activeForm.currentColumn
                : activeForm.columns.find(c => c.ID === relatedField.columnId);
            if (activeField) {
                const field = mapValueByColumnType(activeField.currentValue, currentColumn);
                const recordUpdateRes = await updateFieldValue(activeForm, field);
                // if message has error or warning message, we should let user to fix & update current value on the server
                this.checkRecordErrorState(recordUpdateRes.Messages);
                const isValid =
                    checkDuplicates && recordUpdateRes.Messages.length === 0
                        ? this.checkForDuplicateEntries(activeForm, recordUpdateRes)
                        : true;
                if (isValid) {
                    return await handleScreenMessage(
                        recordUpdateRes,
                        {
                            onClose: async () => {
                                // if it's an update of related field, we need to re-set its value on the server
                                if (relatedField) {
                                    const field = mapValueByColumnType(
                                        activeField.origValue,
                                        currentColumn
                                    );
                                    await updateFieldValue(activeForm, field);
                                    this.checkRecordErrorState(recordUpdateRes.Messages);
                                }
                            },
                            onErrorAccept: () => {
                                // if it's an update of related field, we need to focus to it
                                // we need to skip validation in order to avoid triggering another field update
                                if (relatedField) {
                                    const {tableAPI, currentRow} = this.getActiveForm();
                                    tableAPI?.focusCellWithoutValidation(
                                        currentColumn.index,
                                        currentRow
                                    );
                                }
                            }
                        },
                        async res => {
                            this.editMode = RecordEditState.update;
                            const updateRes = res || recordUpdateRes;
                            activeForm.updateRecordValues(
                                activeForm.currentRow,
                                updateRes.Record.Fields
                            );
                            const {tableAPI, currentRow} = this.getActiveForm();
                            tableAPI?.focusCell(currentColumn.index, currentRow);
                        },
                        false,
                        this.session
                    );
                }
            }
            return APIActionStatus.blocked;
        }
        changeLockerStatus(this.session, UpdateStatusTypes.none, false);
        return APIActionStatus.blocked;
    };

    checkForDuplicateEntries = (
        activeForm: Form,
        fieldUpdateRs: priority.netitems.form.FieldUpdateRs
    ): APIActionStatus => {
        const columnValue = activeForm.activeField().currentValue;
        if (activeForm?.currentColumn?.Key && columnValue) {
            const keyColumns = activeForm.columns
                .filter(column => column.Key)
                .map(column => column.id);

            const keyFields = fieldUpdateRs.Record.Fields.filter(field =>
                keyColumns.includes(Number(field.ColumnID))
            );

            // Check if there exists at least one record whose all key fields values
            // are the same as values in keyFields array
            const repeatRecord = activeForm.records.filter(record => {
                // Skip the current record being edited
                if (record.recordIndex === activeForm.currentRow) {
                    return false;
                }
                const compNameColumn = activeForm.columns.find(c => c.MultiEnv);
                const activeFieldCompValue = activeForm.isMultiComp
                    ? activeForm.currentRecord.getField(compNameColumn.id).currentValue
                    : null;
                // Check if all key field values match
                return keyFields.every(keyField => {
                    const recordField = record.getField(Number(keyField.ColumnID));
                    const column = activeForm.getColumnByID(Number(keyField.ColumnID));

                    // Convert IConstant to comparable string value using getOrigValue
                    const keyFieldValue = getOrigValue(keyField.Value, column);
                    const recordFieldValue = recordField?.currentValue;

                    const compNameValue = activeForm.isMultiComp
                        ? record.getField(compNameColumn.id).currentValue
                        : null;
                    // Both values should be non-empty and equal
                    return (
                        keyFieldValue !== null &&
                        keyFieldValue !== '' &&
                        recordFieldValue !== null &&
                        recordFieldValue !== '' &&
                        keyFieldValue === recordFieldValue &&
                        activeFieldCompValue === compNameValue
                    );
                });
            });

            if (repeatRecord && repeatRecord.length > 0) {
                showFormClientSideMessage(
                    activeForm.session,
                    priority.netitems.shared.Message.Type.Information,
                    {
                        title: texts.record_already_exist,
                        confirmButtonText: texts.ok
                    }
                );
                return APIActionStatus.blocked;
            }
        }
        return APIActionStatus.passed;
    };

    checkRecordErrorState = messages => {
        const MessageType = priority.netitems.shared.Message.Type;
        const currentMessage = messages[0];

        if (currentMessage) {
            if (currentMessage.MType === MessageType.Error) {
                this.editMode = RecordEditState.error;
            } else if (currentMessage.MType === MessageType.Warning) {
                this.editMode = RecordEditState.shouldUpdate;
            }
        }
    };

    handleFormLevel = async (level: number) => {
        const activeForm = this.getActiveForm();
        if (!activeForm) return APIActionStatus.blocked;

        const isSubform = activeForm.parentForm;

        if (
            isSubform &&
            activeForm.currentRecord?.isTemporary &&
            this.editMode === RecordEditState.default
        ) {
            this.removeRecordFromClient(activeForm.currentRow);
        }

        // change focus between parent/subform;
        return await callLinkedActions(
            async () => await this.checkLevel(level),
            async () => {
                if (activeForm.searchModeLevel > 0) {
                    activeForm.setSearchModeLevel(0);
                }
                return this.setActiveLevel(level);
            },
            async () => {
                if (activeForm.parentForm?.level == level) {
                    activeForm.resetFilters();
                    activeForm.resetSort();
                }
                return APIActionStatus.passed;
            }
        );
    };

    runSubForm = async (
        name: string,
        parentForm?: Form,
        isHtmlForm?: boolean,
        runInSearchMode?: boolean
    ) => {
        if (!parentForm) return APIActionStatus.blocked;

        return await callLinkedActions(
            async () => await ensureKeysPresent(parentForm),
            async () => await this.checkLevel(parentForm?.level || 0),
            async () => {
                return await openSubForm(parentForm, name, isHtmlForm);
            },
            async () => {
                if (runInSearchMode) {
                    parentForm.activeSubForm?.setSearchModeLevel(1);
                }
                return APIActionStatus.passed;
            }
        );
    };

    toggleSearchMode = async (level: number) => {
        return await callLinkedActions(
            async () => await this.checkAndSave(),
            async () => {
                const activeForm = this.getActiveForm();
                if (activeForm) {
                    const {setSearchModeLevel} = activeForm;
                    setSearchModeLevel(level);
                    activeForm.activeSubForm?.resetRecords();
                }

                return APIActionStatus.passed;
            }
        );
    };

    @action
    setActiveLevel = async (newLevel: number) => {
        const isLevelChanged = newLevel !== this.activeLevel;
        if (isLevelChanged) {
            this.activeLevel = newLevel;
            newLevel >= 0 && this.setBreadcrumbs(); //-1 level need for remove focus from table, but in this case we dont need re build breadcrumbs
            if (this.getActiveForm()?.refreshCode) {
                await refreshByRefreshLines(this.getActiveForm(), this.getActiveForm().refreshCode);
                this.getActiveForm().refreshCode = null;
            }
        }
        if (formsStore.getActiveForm() && !formsStore.getActiveForm().isPanelTypeSubform()) {
            formsStore.newFormSidePanelSubforms = formsStore.getActiveForm().sidePanelSubforms;
        }
        return APIActionStatus.passed;
    };

    buildBreadcrumbs = (screen, breadCrumbsArr) => {
        const b = mapFormToBreadcrumbs(screen.form, screen.activeLevel);
        const breadcrumbs = [...b, ...breadCrumbsArr];
        if (screen.zoomSource) {
            return this.buildBreadcrumbs(screen.zoomSource, breadcrumbs);
        } else {
            return breadcrumbs;
        }
    };

    @action
    setBreadcrumbs = () => {
        this.breadcrumbs = this.buildBreadcrumbs(this, []);
    };

    removeRecordFromClient = (rowNumber: number) => {
        const form = this.getActiveForm();

        form.records = form.records
            .filter(record => record.recordIndex !== rowNumber)
            .map((record, index) => {
                record.setRecordIndex(index);
                return record;
            });
    };
}
